import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { get } from 'lodash';
import * as R from 'remeda';
import { useQueryClient } from '@tanstack/react-query';
import update from 'immutability-helper';
import { useIntl } from 'react-intl';
import { useHistory, useParams } from 'react-router';
import { SchemaDatapointEnumOption } from '@rossum/api-client/schemas';
import { ColumnHeaderPaper } from '@rossum/components/data-grid/ColumnHeaderPaper';
import { commonDataGridStyles } from '@rossum/components/data-grid/styles';
import { LeavingDialog } from '@rossum/components/leaving-dialog/LeavingDialog';
import { PageLayout } from '@rossum/components/page-layout/PageLayout';
import { IconTrash } from '@rossum/ui/icons/tabler';
import {
  Box,
  formControlClasses,
  outlinedInputClasses,
  Stack,
  SvgIcon,
} from '@rossum/ui/material';
import {
  DataGridPro,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridCellEditStartReasons,
  GridCellEditStopReasons,
  gridClasses,
  gridFilteredSortedRowIdsSelector,
  GridRowId,
  GridRowSelectionModel,
  GridValidRowModel,
  useGridApiRef,
} from '@rossum/ui/x-data-grid-pro';
import { useDataGridLocaleText } from '../../../../i18n/useDataGridLocaleText';
import { HelmetComponent } from '../../../../routes/HelmetComponent';
import { useFieldManagerDataContext } from '../../hooks/FieldManagerDataContext';
import { usePatchSchemas } from '../../hooks/usePatchSchemas';
import { aggregationRow } from '../constants';
import { getFieldTitleFromPath } from '../routes';
import { filterPanelProps } from '../ui/dataGridStyles';
import { getColumns } from './columns/columns';
import { EnumAndFormulaCellValue, ExtendedCellParams } from './columns/types';
import {
  batchEditingActive,
  cellEditabilityDisabled,
  dataGridStyles,
  rowHasError,
} from './dataGridStyles';
import {
  DataForBulkEditDrawer,
  EditColumnDrawer,
} from './enum-formula-editing/EditColumnDrawer';
import { EnumDrawer } from './enum-formula-editing/EnumDrawer';
import { FormulaDrawer } from './enum-formula-editing/FormulaDrawer';
import { LookupDrawer } from './enum-formula-editing/LookupDrawer';
import { ReasoningDrawer } from './enum-formula-editing/ReasoningDrawer';
import { ErrorMessageWithDialog } from './errors/ErrorMessageWithDialog';
import { FieldDetailHeader } from './FieldDetailHeader';
import { getDataForAggregationRow } from './getDataForAggregationRow';
import { getProcessRowUpdate, UpdatedSchemasMap } from './getProcessRowUpdate';
import { useCopyPasteFieldSettings } from './hooks/useCopyPasteFieldSettings';
import { useDuplicateFieldToQueues } from './hooks/useDuplicateFieldToQueues';
import { useRemoveFromQueues } from './hooks/useRemoveFromQueues';
import { removeItemFromSchema } from './removeItemFromSchema';
import { transformSchemasToRows } from './rows/rows';
import { GridRowModel } from './rows/rowTypes';
import { SelectionPanel } from './selection/SelectionPanel';

const getRowId = (row: GridRowModel) => row.meta.schema_id;

export const FieldDetail = () => {
  const params = useParams<{ schemaId: string }>();
  const intl = useIntl();
  const { schemaId } = params;
  const history = useHistory();
  const localeText = useDataGridLocaleText();

  const { flatSchemasWithQueues, schemas, queues, workspaces, isLoading } =
    useFieldManagerDataContext();

  const [rows, setRows] = useState<Array<GridRowModel>>(
    transformSchemasToRows(schemaId, flatSchemasWithQueues)
  );

  const [filteredRows, setFilteredRows] = useState(rows);

  // ugly but easiest solution for synchronization of filteredRows with rows
  useEffect(() => {
    setFilteredRows(rows);
  }, [rows]);

  const [optionsForEnumDialog, setOptionsForEnumDialog] = useState<{
    options: SchemaDatapointEnumOption[];
    value: EnumAndFormulaCellValue;
  } | null>(null);

  const [formulaForFormulaDrawer, setFormulaForFormulaDrawer] = useState<{
    formula: string;
    value: EnumAndFormulaCellValue;
  } | null>(null);

  const [reasoningForDrawer, setReasoningForDrawer] = useState<{
    prompt: string;
    context: string[];
    value: EnumAndFormulaCellValue;
  } | null>(null);

  const [lookupForDrawer, setLookupForDrawer] = useState<{
    matching: Record<string, unknown>;
    value: EnumAndFormulaCellValue;
  } | null>(null);

  const [dataForBulkEditDrawer, setDataForBulkEditDrawer] =
    useState<DataForBulkEditDrawer>(null);

  // I would like to find a nicer solution to an issue when rows are empty after doing refresh on a detail page
  // but this one is functional
  useEffect(() => {
    const updatedRows = transformSchemasToRows(
      schemaId,
      flatSchemasWithQueues
    )();
    setRows(updatedRows);
  }, [schemaId, flatSchemasWithQueues]);

  const [updatedSchemas, setUpdatedSchemas] = useState<UpdatedSchemasMap>({});

  const schemasToSave = Object.values(updatedSchemas);
  const schemasToSaveLength = schemasToSave.length;
  const hasNoChanges = schemasToSaveLength === 0;

  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>({ type: 'include', ids: new Set() });

  const selectionActive = rowSelectionModel.ids.size > 0;

  const selectedRows = useMemo(
    () => rows.filter(row => rowSelectionModel.ids.has(row.meta.schema_id)),
    [rows, rowSelectionModel]
  );

  const [editingField, setEditingField] = useState<string | null>(null);
  const [batchEditError, setBatchEditError] = useState<boolean>(false);

  const shouldWarnUser = !(editingField === null && hasNoChanges);

  const gridApiRef = useGridApiRef();

  const queryClient = useQueryClient();

  const processRowUpdate = useCallback(
    (next: GridRowModel, prev: GridRowModel) => {
      const processUpdate = getProcessRowUpdate({
        setRows,
        setUpdatedSchemas,
        schemas: flatSchemasWithQueues,
      });

      const shouldBatchUpdate = selectionActive && editingField !== null;

      if (shouldBatchUpdate) {
        gridApiRef.current
          // TODO: This is deprecated and not sure if it will work
          ?.getSelectedRows()
          // @ts-expect-error GridValidRowModel returned from getSelectedRows is less strict version of GridRowModel
          .forEach(row => processUpdate(row));

        setEditingField(null);
      }

      // processRowUpdate must return updated row
      return processUpdate(next, prev);
    },
    [flatSchemasWithQueues, selectionActive, editingField, gridApiRef]
  );

  const removeFromSchema = useCallback(
    (row: GridRowModel) => {
      const { schema_id } = row.meta;

      if (schema_id === aggregationRow) {
        return;
      }

      setUpdatedSchemas(prevState => {
        const alreadyInUpdated = prevState[schema_id];
        const originalSchema = flatSchemasWithQueues.find(
          schema => schema.id === schema_id
        );
        const schemaToUpdate = alreadyInUpdated ?? originalSchema;

        if (!schemaToUpdate) {
          return prevState;
        }

        const schemaWithoutRemoved = removeItemFromSchema(schemaToUpdate, row);

        return {
          ...prevState,
          [schema_id]: schemaWithoutRemoved,
        };
      });

      setRows(prevState =>
        prevState.filter(r => r.meta.schema_id !== schema_id)
      );
    },
    [flatSchemasWithQueues]
  );

  const { mutation, taskQueue } = usePatchSchemas();

  const handleSubmitChanges = () => {
    mutation.mutate(schemasToSave, {
      onSuccess: ({ fulfilled }) => {
        setUpdatedSchemas(prev =>
          update(prev, { $unset: fulfilled.map(({ id }) => id) })
        );

        fulfilled.forEach(schema => {
          queryClient.setQueryData(['schema', schema.url], schema);
        });
      },
    });
  };

  const { setRemoveDialogParams, removeDialog } = useRemoveFromQueues({
    removeFromSchema,
  });

  const { setDuplicateDialogParams, duplicateDialog } =
    useDuplicateFieldToQueues({
      setUpdatedSchemas,
      setRows,
      queues,
      workspaces,
      flatSchemasWithQueues,
      schemas,
      updatedSchemas,
    });

  const { copiedRow, setCopiedRow, pasteFieldConfigurationToRows } =
    useCopyPasteFieldSettings({
      flatSchemasWithQueues,
      queues,
      schemas,
      updatedSchemas,
      setUpdatedSchemas,
      setRows,
    });

  const visibleColumnsPerRows = useMemo(
    () =>
      getColumns({
        openRemoveModal: setRemoveDialogParams,
        setOptionsForEnumDialog,
        setFormulaForFormulaDrawer,
        setReasoningForDrawer,
        setLookupForDrawer,
        openDuplicateModal: setDuplicateDialogParams,
        setCopiedRow,
        copiedRow,
        pasteFieldConfigurationToRows,
        intl,
        setBatchEditError,
      }).filter(
        ({ editabilityCondition }) =>
          editabilityCondition === undefined ||
          rows.some(row => editabilityCondition(row.meta))
      ),
    [
      setRemoveDialogParams,
      setDuplicateDialogParams,
      setCopiedRow,
      copiedRow,
      pasteFieldConfigurationToRows,
      intl,
      rows,
      setReasoningForDrawer,
      setLookupForDrawer,
    ]
  );

  const pinnedRows = useMemo(
    () =>
      rows.length === 0
        ? undefined
        : {
            bottom: [
              {
                // returned value from getDataForAggregationRow is of a different type than GridRowModel
                // and getting of values for Aggregations row is tailored for its needs
                ...getDataForAggregationRow(
                  visibleColumnsPerRows,
                  filteredRows
                ),
                id: '0',
                meta: {
                  schema_id: aggregationRow,
                  path: [],
                  setDataForBulkEditDrawer,
                },
              } as unknown as GridRowModel,
            ],
          },
    [filteredRows, rows.length, visibleColumnsPerRows]
  );

  const editableColumnsForSelectedRows = useMemo(() => {
    if (selectedRows.length === 0) {
      return [];
    }

    return visibleColumnsPerRows.flatMap(column => {
      const { editable, editabilityCondition, field } = column;

      // "options" and "formula" fields are special, because they are not editable in the table cell itself, but only batchEditable from the selection panel
      if (field === 'options' || field === 'formula') {
        return {
          ...column,
          isBatchEditable: editabilityCondition
            ? selectedRows.every(row => editabilityCondition(row.meta))
            : false,
        };
      }

      return editable
        ? {
            ...column,
            isBatchEditable: editabilityCondition
              ? selectedRows.every(row => editabilityCondition(row.meta))
              : !!column.editable,
          }
        : [];
    });
  }, [selectedRows, visibleColumnsPerRows]);

  const selectedRowIds = useMemo(
    () => selectedRows.map(row => row.meta.schema_id),
    [selectedRows]
  );

  const prevRowValues = useRef<GridValidRowModel[] | null>(null);
  const startBatchEditMode = useCallback(
    ({ id, field }: { id: GridRowId; field: string }) => {
      prevRowValues.current = Array.from(
        gridApiRef.current?.getSelectedRows().values() ?? []
      );
      setEditingField(field);

      gridApiRef.current?.startCellEditMode({
        id,
        field,
      });
    },
    [gridApiRef]
  );

  const revertBatchEdits = useCallback(() => {
    if (prevRowValues.current !== null) {
      gridApiRef.current?.updateRows(prevRowValues.current);
    }
    setBatchEditError(false);
    setEditingField(null);
  }, [gridApiRef]);

  const mutationErrorIds = useMemo(
    () =>
      mutation.data?.rejected.map(rejected => {
        const id = get(rejected, 'id');
        return id as number | undefined;
      }) ?? [],
    [mutation.data?.rejected]
  );

  // synchronize state after schemas update
  // using reset with changing key cannot be used because we want to preserve other states (such as selection, sorting etc.)
  useEffect(() => {
    if (mutationErrorIds !== undefined) {
      setRows(prevRows => {
        const newRows = transformSchemasToRows(
          schemaId,
          flatSchemasWithQueues
        )();
        const prevRowsMap = R.fromEntries(
          prevRows.map(row => [row.meta.schema_id, row])
        );

        return newRows.map(newRow => {
          if (newRow.meta.schema_id === 'aggregation-row') {
            return newRow;
          }

          return mutationErrorIds.includes(newRow.meta.schema_id)
            ? // keep previous row state in case of the error
              (prevRowsMap[newRow.meta.schema_id] ?? newRow)
            : newRow;
        });
      });
    }
  }, [flatSchemasWithQueues, schemaId, mutationErrorIds]);

  const fieldTitle = getFieldTitleFromPath(history.location.pathname);

  useEffect(() => {
    const handleFilterUpdate = () => {
      const filteredRowIds = gridFilteredSortedRowIdsSelector(gridApiRef);
      const newRows: GridRowModel[] = filteredRowIds.flatMap(
        id => gridApiRef.current?.getRow(id) ?? []
      );

      if (!isLoading)
        setFilteredRows(prev =>
          !R.isDeepEqual(prev, newRows) ? newRows : prev
        );
    };

    return gridApiRef.current?.subscribeEvent(
      'filteredRowsSet',
      handleFilterUpdate
    );
  }, [gridApiRef, isLoading]);

  return (
    <PageLayout
      fullWidth
      renderHeader={headerParams => (
        <FieldDetailHeader
          mutationIsLoading={mutation.isLoading}
          taskQueue={taskQueue}
          schemasToSaveLength={schemasToSaveLength}
          handleSubmitChanges={handleSubmitChanges}
          {...headerParams}
        />
      )}
    >
      {fieldTitle ? (
        <HelmetComponent
          dynamicName={fieldTitle}
          translationKey="features.routes.pageTitles.settings.fieldManager.details"
        />
      ) : null}
      <Stack px={4} pt={4} height={1} flex={1}>
        {/* Box is needed to prevent screen from jumping when SelectionPanel changes visibility */}
        <Box>
          <SelectionPanel
            visible={selectionActive}
            selectedRows={selectedRows}
            setRowSelectionModel={setRowSelectionModel}
            editableColumns={editableColumnsForSelectedRows}
            startBatchEditMode={startBatchEditMode}
            openDeleteModal={setRemoveDialogParams}
            gridApiRef={gridApiRef}
            shouldDisplayPasteOption={!!copiedRow}
            pasteFieldConfigurationToRows={pasteFieldConfigurationToRows}
            setDataForBulkEditDrawer={setDataForBulkEditDrawer}
          />
        </Box>
        <Stack spacing={2} direction="row" alignItems="center">
          {(mutation.isError || !!mutation.data?.rejected.length) && (
            <ErrorMessageWithDialog
              rejectedRequests={mutation.data?.rejected}
              schemas={flatSchemasWithQueues}
            />
          )}
        </Stack>
        <Box
          sx={{ ...dataGridStyles, position: 'relative', height: 1, flex: 1 }}
        >
          <DataGridPro<GridRowModel>
            disableRowSelectionExcludeModel
            disableColumnSelector
            apiRef={gridApiRef}
            columns={visibleColumnsPerRows}
            localeText={localeText}
            rows={rows}
            loading={isLoading}
            disableColumnReorder
            disableRowSelectionOnClick
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: t => t.palette.background.default,
              [`& .${gridClasses.columnHeader}`]: {
                backgroundColor: t => t.palette.background.default,
              },
              [`& .${gridClasses['cell--pinnedLeft']}`]: {
                backgroundColor: t => t.palette.background.default,
              },
              [`& .${gridClasses['filler--pinnedLeft']}`]: {
                background: t => t.palette.background.default,
              },
              [`& .${gridClasses['cell--pinnedRight']}`]: {
                backgroundColor: t => t.palette.background.default,
              },
              [`& .${gridClasses['filler--pinnedRight']}`]: {
                background: t => t.palette.background.default,
              },
              [`& .${gridClasses.row}:hover,
                & .${gridClasses.row}:hover .${gridClasses.cell}.${gridClasses['cell--pinnedRight']},
                & .${gridClasses.row}:hover .${gridClasses.cell}.${gridClasses['cell--pinnedLeft']}`]:
                {
                  backgroundColor: t =>
                    `${t.palette.background.paper} !important`,
                  backgroundImage: 'none !important',
                },
              ...commonDataGridStyles,
            }}
            hideFooter
            checkboxSelection
            onRowSelectionModelChange={newRowSelectionModel => {
              setRowSelectionModel(newRowSelectionModel);
            }}
            initialState={{
              pinnedColumns: {
                right: ['actions'],
                left: [GRID_CHECKBOX_SELECTION_COL_DEF.field],
              },
            }}
            rowSelectionModel={rowSelectionModel}
            disableColumnMenu={selectionActive}
            processRowUpdate={processRowUpdate}
            getRowId={getRowId}
            pinnedRows={pinnedRows}
            slots={{
              filterPanelDeleteIcon: () => (
                <SvgIcon fontSize="medium">
                  <IconTrash />
                </SvgIcon>
              ),
              columnHeaders: ColumnHeaderPaper,
            }}
            slotProps={{
              loadingOverlay: {
                variant: 'linear-progress',
              },
              baseSelectOption: {
                // @ts-expect-error sx is not in the type definition but it works,
                sx: {
                  // workaround for making first empty option having the same height as other options
                  '&:after': { content: "' '" },
                },
              },
              baseFormControl: {
                sx: {
                  // workaround for making singleSelect input filter full width
                  [`> * .${formControlClasses.root}`]: {
                    width: '100%',
                  },
                },
              },

              baseSelect: {
                native: false,
                // @ts-expect-error sx is not in the type definition but it works,
                sx: {
                  [`.${gridClasses.cell} & .${outlinedInputClasses.notchedOutline}`]:
                    {
                      border: 'unset !important',
                    },

                  [`& .${outlinedInputClasses.input}`]: {
                    pl: 2,
                  },
                },
              },
              filterPanel: filterPanelProps,
            }}
            onCellEditStop={({ reason, id, field }, e) => {
              if (
                selectionActive &&
                reason === GridCellEditStopReasons.escapeKeyDown
              ) {
                revertBatchEdits();
                return;
              }

              // revert edits on "blur" in case there is an error
              if (
                batchEditError &&
                reason !== GridCellEditStopReasons.enterKeyDown
              ) {
                e.defaultMuiPrevented = true;
                gridApiRef.current?.stopCellEditMode({
                  id,
                  field,
                  ignoreModifications: true,
                });
                revertBatchEdits();
              }
            }}
            // onCellEditStart is not triggered when using gridApiRef.startCellEditMode
            onCellEditStart={({ reason, id, field }, e) => {
              // do not start edit on printable keydown - it breaks usage of Autocomplete because value is not an array
              if (reason === GridCellEditStartReasons.printableKeyDown) {
                e.defaultMuiPrevented = true;
                return;
              }

              if (selectionActive) {
                if (
                  // do not start edit when batch editing is already active
                  editingField !== null ||
                  // do not start edit when cell is not editable
                  !editableColumnsForSelectedRows.some(
                    c => c.field === field && c.isBatchEditable
                  ) ||
                  // do not start edit when cell is not in selected row
                  !selectedRowIds.includes(Number(id))
                ) {
                  e.defaultMuiPrevented = true;
                  return;
                }

                // enter batch edit mode on focused cell
                if (editingField === null) {
                  e.defaultMuiPrevented = true;

                  startBatchEditMode({ id, field });
                }
              }
            }}
            isCellEditable={(cellParams: ExtendedCellParams) =>
              cellParams.id !== aggregationRow &&
              // control editability of cells based on the condition from column definition
              (cellParams.colDef.editabilityCondition?.(cellParams.row.meta) ??
                true)
            }
            getRowClassName={({ id }) =>
              id && mutationErrorIds?.includes(Number(id)) ? rowHasError : ''
            }
            getCellClassName={(cellParams: ExtendedCellParams) => {
              if (
                selectedRowIds.includes(Number(cellParams.id)) &&
                cellParams.field === editingField
              ) {
                return batchEditingActive;
              }

              // highlight originally editable cells that are not editable due to editabilityCondition
              return cellParams.colDef?.editable &&
                !cellParams.isEditable &&
                cellParams.id !== aggregationRow
                ? cellEditabilityDisabled
                : '';
            }}
          />
          {removeDialog}
          <LeavingDialog when={shouldWarnUser} />
          <EnumDrawer
            onClose={() => setOptionsForEnumDialog(null)}
            optionsForEnumDialog={optionsForEnumDialog}
          />
          <FormulaDrawer
            onClose={() => setFormulaForFormulaDrawer(null)}
            formulaForDrawer={formulaForFormulaDrawer}
          />
          <ReasoningDrawer
            onClose={() => setReasoningForDrawer(null)}
            reasoningForDrawer={reasoningForDrawer}
          />
          <LookupDrawer
            onClose={() => setLookupForDrawer(null)}
            lookupForDrawer={lookupForDrawer}
          />
          {dataForBulkEditDrawer ? (
            <EditColumnDrawer
              key={dataForBulkEditDrawer.field}
              onClose={() => setDataForBulkEditDrawer(null)}
              dataForBulkEditDrawer={dataForBulkEditDrawer}
              setUpdatedSchemas={setUpdatedSchemas}
              setRows={setRows}
              rows={rows}
              flatSchemasWithQueues={flatSchemasWithQueues}
            />
          ) : null}
          {duplicateDialog}
        </Box>
      </Stack>
    </PageLayout>
  );
};
